import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import logo from '@/assets/logo-insane-force.jpg';
import { Mail, Lock, User, ArrowRight, ChevronLeft, UserPlus, MailCheck } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PasswordInput } from '@/components/PasswordInput';
import { Link } from 'react-router-dom';

export default function Auth() {
  const { user, loading } = useAuth();
  const { isCoachOrAdmin, loading: roleLoading } = useUserRole();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const coachAccess = searchParams.get('coach') === '1';

  const [isLogin, setIsLogin] = useState(tabParam !== 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  // Después de un signup exitoso (sin sesión, requiere email confirm),
  // mostramos una vista dedicada en lugar del toast efímero. Evita que
  // el usuario dé doble-click pensando que no funcionó.
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLogin(tabParam !== 'register');
  }, [tabParam]);

  const setLoginIntent = (intent: 'login' | 'register') => {
    sessionStorage.setItem('auth_intent', intent);
  };

  useEffect(() => {
    if (!user || roleLoading) return;

    const checkOnboardingAndRedirect = async () => {
      const intent = sessionStorage.getItem('auth_intent');
      sessionStorage.removeItem('auth_intent');

      if (intent === 'login') {
        const { data: onboarding } = await supabase
          .from('client_onboarding_profiles')
          .select('onboarding_completed')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!onboarding?.onboarding_completed) {
          await supabase.auth.signOut();
          toast({
            title: 'Cuenta no encontrada',
            description: 'No tienes una cuenta registrada. Ve a "Regístrate" para crear una.',
            variant: 'destructive',
          });
          navigate('/auth?tab=register', { replace: true });
          return;
        }
      }

      if (coachAccess && !isCoachOrAdmin) {
        toast({
          title: 'Acceso Restringido',
          description: 'Tu cuenta no tiene permisos de Coach o Administrador.',
          variant: 'destructive',
        });
      }

      navigate('/', { replace: true });
    };

    checkOnboardingAndRedirect();
  }, [user, roleLoading, isCoachOrAdmin, coachAccess, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-muted-foreground">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Resuelve la URL a la que debe regresar el usuario después de
  // confirmar email o completar Google OAuth.
  //
  // Si hay un invite token pendiente (cliente viene de /invite/:token),
  // regresa al /invite/:token para que pueda aceptar la invitación.
  // Si no, va al /auth/callback default.
  //
  // Esto resuelve el edge case cross-device: el email link es
  // independiente de localStorage del primer dispositivo, así que el
  // path debe codificarse en la propia URL del email.
  const resolvePostAuthUrl = (): string => {
    try {
      const token = localStorage.getItem('iftmx:pending-invite-token');
      if (token) return `${window.location.origin}/invite/${token}`;
    } catch {}
    return `${window.location.origin}/auth/callback`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: displayName },
            // Cross-device safe: si confirma email en otro dispositivo,
            // el link los regresa al /invite/:token si había uno
            // pendiente (incluso si localStorage no sobrevive).
            emailRedirectTo: resolvePostAuthUrl(),
          },
        });
        if (error) throw error;
        // Si NO hay sesión, Supabase requiere email confirmation.
        // Cambiamos a la vista "revisa tu correo" — toast invisible
        // hacía que los usuarios dieran doble-click y trigerearan
        // rate-limit de Supabase.
        if (!data.session) {
          setSignedUpEmail(email);
        }
      }
    } catch (error: any) {
      const rawMsg = error?.message || '';
      const isShortRateLimit = /you can only request this after/i.test(rawMsg);
      const isEmailRateLimit = /email rate limit exceeded/i.test(rawMsg);
      const friendlyMsg = isShortRateLimit
        ? 'Espera unos segundos antes de volver a intentar — ya recibimos tu solicitud.'
        : isEmailRateLimit
        ? 'Demasiados correos enviados en la última hora. Intenta con Google o espera 1 hora.'
        : rawMsg || 'Intenta de nuevo en un momento';
      toast({ title: 'Error', description: friendlyMsg, variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    setSocialLoading(provider);
    setLoginIntent(isLogin ? 'login' : 'register');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: resolvePostAuthUrl(),
        },
      });
      if (error) throw error;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setSocialLoading(null);
    }
  };

  // Vista post-signup: bienvenida aspiracional + CTA claro de revisar
  // correo. Reemplaza toda la pantalla — imposible perder el mensaje.
  if (signedUpEmail) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 bg-[#0a0a0a]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[70%] h-full bg-gradient-to-l from-[hsl(var(--accent)/0.15)] via-[hsl(var(--accent)/0.05)] to-transparent" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[hsl(var(--accent)/0.08)] blur-[150px]" />
        </div>
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle variant="navbar" />
        </div>
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-6">
            <img src={logo} alt="Insane Force" className="w-24 h-24 rounded-full border-2 border-accent shadow-2xl shadow-accent/40 mx-auto mb-5" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 border border-accent/40 mb-4">
              <MailCheck className="w-3.5 h-3.5 text-accent" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-accent">A un paso de empezar</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-white mb-3 leading-[1.05]" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {displayName ? <>Bienvenido, <span className="text-accent">{displayName.split(' ')[0]}</span></> : <>Tu transformación <span className="text-accent">empieza aquí</span></>}
            </h1>
            <p className="text-base text-white/80 leading-relaxed">
              Estás a un click de tener a tu coach contigo todos los días, rutinas hechas para ti y comidas que sí funcionan.
            </p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-accent/15 to-accent/5 border-2 border-accent/40 p-5 mb-5 text-center">
            <MailCheck className="w-8 h-8 text-accent mx-auto mb-3" />
            <p className="text-sm text-white/90 mb-2 font-bold">
              Te mandamos un correo de confirmación a
            </p>
            <p className="text-base font-black text-accent break-all mb-3">
              {signedUpEmail}
            </p>
            <p className="text-xs text-white/70 leading-relaxed">
              <strong className="text-white">Haz click en el link del correo</strong> y te llevamos directo a tu cuenta. <span className="text-white/60">Toma 10 segundos.</span>
            </p>
          </div>

          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 mb-5">
            <p className="text-xs text-amber-200/90 text-center">
              <strong>¿No te llega en 2 min?</strong> Revisa spam o correo no deseado.
            </p>
          </div>

          <button
            onClick={() => { setSignedUpEmail(null); setEmail(''); setPassword(''); }}
            className="w-full py-3 rounded-xl bg-secondary/40 border-2 border-secondary text-white text-xs font-black uppercase tracking-wider hover:border-accent/50 transition-all"
          >
            Usar otro correo
          </button>
          <p className="text-center text-xs text-white/40 mt-6">
            ¿Ya tienes cuenta? <Link to="/auth" onClick={() => { setSignedUpEmail(null); setIsLogin(true); }} className="font-bold text-accent hover:underline">Inicia sesión</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 bg-[#0a0a0a]">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[70%] h-full bg-gradient-to-l from-[hsl(var(--accent)/0.15)] via-[hsl(var(--accent)/0.05)] to-transparent" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[hsl(var(--accent)/0.08)] blur-[150px]" />
        <div className="absolute top-1/3 right-0 w-[300px] h-[400px] rounded-full bg-[hsl(var(--accent)/0.12)] blur-[120px]" />
      </div>

      {/* Back button — visible pill in top left */}
      <button
        onClick={() => navigate('/welcome')}
        className="fixed top-4 left-4 z-50 flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-black text-xs uppercase tracking-widest shadow-lg shadow-accent/30 hover:bg-accent/80 transition-all duration-200"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        <ChevronLeft className="w-4 h-4" />
        Volver
      </button>

      {/* Theme toggle — top right */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle variant="navbar" />
      </div>

      <div className="w-full max-w-md relative z-10 mt-16">
        {/* Logo & branding */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-4">
            <div className="absolute -inset-3 bg-accent/20 rounded-full blur-xl animate-pulse" />
            <img
              src={logo}
              alt="Insane Force Team"
              className="relative w-20 h-20 rounded-full object-cover border-4 border-accent shadow-[0_0_30px_hsl(var(--accent)/0.3)]"
            />
          </div>
          <h1 className="text-4xl font-black italic uppercase tracking-tighter text-white"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Insane Force
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-8 h-px bg-accent" />
            <p className="text-accent text-[10px] font-black uppercase tracking-[0.3em]"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Team Portal
            </p>
            <div className="w-8 h-px bg-accent" />
          </div>
        </div>

        {/* Main card */}
        <div className="bg-card/80 backdrop-blur-sm border-2 border-secondary rounded-2xl overflow-hidden shadow-2xl shadow-black/20">
          {/* Gate de signup: si estamos en tab=register pero NO hay un
              invite token pendiente, mostramos mensaje explicando que
              el signup de cliente es solo por invitación. Esto cierra
              el camino al "cliente huérfano" en multi-coach. Los coaches
              usan /coach/signup que es un flujo separado. */}
          {!isLogin && !(typeof window !== 'undefined' && localStorage.getItem('iftmx:pending-invite-token')) && (
            <div className="p-8 text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/15 flex items-center justify-center">
                <UserPlus className="w-8 h-8 text-accent" />
              </div>
              <h2 className="text-lg font-black uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Solo por link de invitación
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Solo puedes registrarte con un link de invitación. Solicita a tu coach que te mande un link de invitación.
              </p>
              <p className="text-xs text-muted-foreground/80 leading-relaxed">
                ¿Ya tienes cuenta? Usa "Iniciar Sesión". ¿Eres coach?{' '}
                <button onClick={() => navigate('/coach/signup')} className="text-accent font-bold hover:underline">
                  Regístrate aquí
                </button>.
              </p>
              <button
                onClick={() => navigate('/auth?tab=login')}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-xs uppercase tracking-widest min-h-[48px]"
              >
                Volver a iniciar sesión
              </button>
            </div>
          )}

          {/* Header — solo si NO estamos en el gate */}
          {(isLogin || (typeof window !== 'undefined' && localStorage.getItem('iftmx:pending-invite-token'))) && (
          <div className="px-8 pt-6 pb-2">
            <h2 className="text-xl font-black uppercase tracking-wider text-accent"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {isLogin ? 'Iniciar Sesión' : 'Crear cuenta de cliente'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {isLogin
                ? 'Bienvenido de vuelta. Ingresa con tu cuenta.'
                : 'Te invitó un coach. Termina el registro para empezar.'}
            </p>
          </div>
          )}

          {/* Form completo (social + email): solo visible si NO estamos
              en el gate. Mismo check que el header. */}
          {(isLogin || (typeof window !== 'undefined' && localStorage.getItem('iftmx:pending-invite-token'))) && (
          <div className="p-8 space-y-6">
            {/* Social login — solo Google. Microsoft/Azure se quitó porque
                el provider no está configurado en Supabase y confundía a
                los clientes con Outlook. Quien tenga Outlook puede usar
                el formulario de email/password de abajo. */}
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              disabled={!!socialLoading}
              className="w-full group flex items-center justify-center gap-2.5 px-4 py-3.5 bg-secondary/50 border-2 border-secondary rounded-xl font-bold text-xs uppercase tracking-wider hover:border-accent/50 hover:bg-accent/5 transition-all disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" className="shrink-0">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span>{socialLoading === 'google' ? '...' : 'Continuar con Google'}</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground">o con email</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="relative">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    name="name"
                    id="display-name"
                    autoComplete="name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 bg-secondary/30 border-2 border-secondary rounded-xl font-bold text-sm placeholder:text-muted-foreground/60 focus:border-accent focus:bg-background focus:outline-none transition-all"
                    placeholder="Tu nombre completo"
                    required
                  />
                </div>
              )}

              {/* autoComplete + name + id + webauthn: estos atributos
                  permiten que iOS Keychain / Android autofill / 1Password
                  reconozcan este form como un login estándar. Resultado:
                  iOS ofrece guardar la contraseña la primera vez (con
                  prompt de Face ID) y autocompletar las siguientes (con
                  Face ID también). El token "webauthn" en autoComplete
                  habilita el "conditional UI" para cuando haya passkeys
                  registradas (feature futura, no rompe nada hoy). */}
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  name="email"
                  id="email"
                  autoComplete={isLogin ? "username webauthn" : "email"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 bg-secondary/30 border-2 border-secondary rounded-xl font-bold text-sm placeholder:text-muted-foreground/60 focus:border-accent focus:bg-background focus:outline-none transition-all"
                  placeholder="tu@email.com"
                  required
                />
              </div>

              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <PasswordInput
                  name="password"
                  id="password"
                  autoComplete={isLogin ? "current-password webauthn" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3.5 bg-secondary/30 border-2 border-secondary rounded-xl font-bold text-sm placeholder:text-muted-foreground/60 focus:border-accent focus:bg-background focus:outline-none transition-all"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={formLoading}
                className="group w-full flex items-center justify-center gap-3 bg-accent text-accent-foreground py-4 rounded-xl font-black uppercase tracking-[0.15em] text-sm hover:shadow-[0_0_30px_hsl(var(--accent)/0.4)] transition-all duration-300 disabled:opacity-50"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {formLoading ? (
                  <div className="w-5 h-5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Entrar' : 'Crear Cuenta'}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Switch hint */}
            <p className="text-center text-xs text-muted-foreground">
              {isLogin ? (
                <>
                  ¿No tienes cuenta?{' '}
                  <button onClick={() => navigate('/auth?tab=register')} className="text-accent font-bold hover:underline">
                    Regístrate aquí
                  </button>
                </>
              ) : (
                <>
                  ¿Ya tienes cuenta?{' '}
                  <button onClick={() => navigate('/auth?tab=login')} className="text-accent font-bold hover:underline">
                    Inicia sesión
                  </button>
                </>
              )}
            </p>
          </div>
          )}
        </div>

        <p className="text-center text-[10px] text-muted-foreground/50 font-bold uppercase tracking-widest mt-8"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          Powered by Insane Force Team © 2026
        </p>
      </div>
    </div>
  );
}
